import React, { createContext, useContext, ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { useRecaptcha } from '@shared/hooks/useRecaptcha';
import { RecaptchaResult } from '@shared/utils/RecaptchaManager';
import { getRecaptchaConfig } from '@shared/utils/recaptchaConfig';
import { RecaptchaRenderer } from '../components/RecaptchaRenderer';
import { setGlobalTokenRefreshFn, registerRecaptchaSiteKey } from '@shared/utils/recaptchaTokenHandler';

interface RecaptchaContextType {
  executeRecaptcha: () => Promise<RecaptchaResult>;
  resetRecaptcha: () => void;
  isReady: () => boolean;
  recaptchaRef: React.RefObject<any>;
  isEnabled: boolean;
  siteKey: string;
  currentToken: string | null;
  setCurrentToken: (token: string | null) => void;
  error: string | null;
  clearError: () => void;
  notifyWidgetLoaded: () => void;
}

const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined);

interface RecaptchaProviderProps {
  children: ReactNode;
}

// reCAPTCHA Enterprise tokens are single-use. No caching is applied.
// Each call to executeRecaptcha() generates a fresh token via Google's API.

/**
 * Creates a promise that resolves when a script loads, with timeout cleanup
 */
function createScriptLoadPromise(
  script: HTMLScriptElement
): Promise<void> {
  return new Promise<void>((scriptResolve, scriptReject) => {
    let resolved = false;
    
    const resolveOnce = () => {
      if (resolved) return;
      resolved = true;
      scriptResolve();
    };
    
    const rejectOnce = (error: Error) => {
      if (resolved) return;
      resolved = true;
      scriptReject(error);
    };
    
    script.onload = () => resolveOnce();
    script.onerror = () => rejectOnce(new Error('Script failed to load'));
  });
}

/**
 * Clear Google reCAPTCHA endpoint cache
 * This function clears the browser cache for Google reCAPTCHA scripts
 * by removing and reloading the script with a cache-busting parameter
 */
function collectRecaptchaScripts(): { sources: string[]; scripts: NodeListOf<Element> } {
  const scripts = document.querySelectorAll('script[src*="google.com/recaptcha"]');
  const sources: string[] = [];
  scripts.forEach((script) => {
    const src = (script as HTMLScriptElement).src;
    if (src) sources.push(src);
  });
  return { sources, scripts };
}

function clearGrecaptchaGlobal(): void {
  if (typeof window === 'undefined' || !(window as any).grecaptcha) return;
  try {
    (window as any).grecaptcha = undefined;
  } catch (e) {
    console.warn('[RecaptchaProvider] Failed to clear grecaptcha:', e);
  }
}

function createCacheBustedScript(originalSrc: string): { script: HTMLScriptElement; loadPromise: Promise<void> } {
  const url = new URL(originalSrc);
  url.searchParams.delete('_');
  url.searchParams.delete('v');
  url.searchParams.delete('cb');
  url.searchParams.set('_', Date.now().toString());

  const newScript = document.createElement('script');
  newScript.src = url.toString();
  newScript.async = true;
  newScript.defer = true;

  const loadPromise = createScriptLoadPromise(newScript);
  return { script: newScript, loadPromise };
}

function createFallbackScript(originalSrc: string): { script: HTMLScriptElement; loadPromise: Promise<void> } {
  const separator = originalSrc.includes('?') ? '&' : '?';
  const newScript = document.createElement('script');
  newScript.src = `${originalSrc}${separator}_=${Date.now()}`;
  newScript.async = true;
  newScript.defer = true;

  const loadPromise = createScriptLoadPromise(newScript);
  return { script: newScript, loadPromise };
}

function reloadScript(originalSrc: string): Promise<void> {
  try {
    const { script, loadPromise } = createCacheBustedScript(originalSrc);
    document.head.appendChild(script);
    return loadPromise.then(() => {
      console.log('[RecaptchaProvider] Reloaded reCAPTCHA script with cache-busting:', script.src);
    });
  } catch (e) {
    console.warn('[RecaptchaProvider] Failed to reload script:', originalSrc, e);
    try {
      const { script, loadPromise } = createFallbackScript(originalSrc);
      document.head.appendChild(script);
      return loadPromise.then(() => {
        console.log('[RecaptchaProvider] Reloaded reCAPTCHA script with cache-busting:', script.src);
      });
    } catch (fallbackError) {
      console.warn('[RecaptchaProvider] Fallback script reload also failed:', fallbackError);
      return Promise.resolve();
    }
  }
}

function clearRecaptchaCache(): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const { sources, scripts } = collectRecaptchaScripts();
      scripts.forEach((script) => script.remove());
      clearGrecaptchaGlobal();

      if (sources.length === 0) {
        console.log('[RecaptchaProvider] No reCAPTCHA scripts to reload');
        resolve();
        return;
      }

      const loadPromises = sources.map(src => reloadScript(src));
      Promise.all(loadPromises).then(() => resolve()).catch((err) => {
        console.warn('[RecaptchaProvider] Error waiting for scripts to reload:', err);
        resolve();
      });
    } catch (error) {
      console.warn('[RecaptchaProvider] Failed to clear reCAPTCHA cache:', error);
      reject(error);
    }
  });
}

export const RecaptchaProvider: React.FC<RecaptchaProviderProps> = ({ children }) => {
  const [currentToken, setCurrentTokenState] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  
  const recaptchaConfig = useMemo(() => {
    const config = getRecaptchaConfig();
    console.log('[RecaptchaProvider] Config loaded:', { 
      enabled: config.enabled, 
      siteKey: config.siteKey ? `${config.siteKey.substring(0, 10)}...` : 'empty',
      envOn: import.meta.env.REACT_APP_RECAPTCHA_ON,
      envKey: import.meta.env.ANTI_BOT_PROVIDER_ID ? `${import.meta.env.ANTI_BOT_PROVIDER_ID.substring(0, 10)}...` : 'empty'
    });
    return config;
  }, []);

  const setCurrentToken = useCallback((token: string | null) => {
    console.log('[RecaptchaProvider] Setting current token:', token ? `${token.substring(0, 20)}...` : 'null');
    setCurrentTokenState(token);
    currentTokenRef.current = token;
  }, []);

  // Use ref to track current token to avoid dependency issues
  const currentTokenRef = React.useRef<string | null>(null);
  const isExecutingRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    currentTokenRef.current = currentToken;
  }, [currentToken]);

  // Single instance of reCAPTCHA for the entire application
  const { recaptchaRef, execute: executeRecaptchaBase, reset: resetRecaptchaBase, isReady } = useRecaptcha({
    siteKey: recaptchaConfig.siteKey,
    enabled: recaptchaConfig.enabled,
    timeout: 30000, // 30 seconds
    maxRetries: 3,
    retryDelay: 1000,
    action: 'LOGIN', // Default action for reCAPTCHA Enterprise
    onSuccess: (token) => {
      setError(null);
      if (isExecutingRef.current) return;
      setCurrentTokenState(token);
    },
    onError: (error) => {
      console.error('[RecaptchaProvider] reCAPTCHA execution failed:', error);
      setError(error);
      if (isExecutingRef.current) return;
      setCurrentTokenState(null);
    },
    onRetry: async (retryCount) => {
      console.warn('[RecaptchaProvider] reCAPTCHA retry attempt:', retryCount);
      clearRecaptchaCache().catch((error) => {
        console.warn('[RecaptchaProvider] Cache clearing failed during retry:', error);
      });
    }
  });

  const handleExecuteSuccess = useCallback((token: string) => {
    setError(null);
    setCurrentTokenState(token);
    currentTokenRef.current = token;
  }, []);

  const handleExecuteFailure = useCallback((result: RecaptchaResult) => {
    if (result.error) setError(result.error);
    setCurrentTokenState(null);
  }, []);

  const executeRecaptcha = useCallback(async (): Promise<RecaptchaResult> => {
    // Always generate a fresh token - reCAPTCHA Enterprise tokens are single-use
    console.log('[RecaptchaProvider] Generating fresh token');
    isExecutingRef.current = true;
    try {
      const result = await executeRecaptchaBase();
      if (result.success && result.token) {
        handleExecuteSuccess(result.token);
      } else {
        handleExecuteFailure(result);
      }
      return result;
    } finally {
      isExecutingRef.current = false;
    }
  }, [executeRecaptchaBase, handleExecuteSuccess, handleExecuteFailure]);

  // Register global token refresh function for use by request functions
  useEffect(() => {
    const tokenRefreshFn = async (): Promise<string | null> => {
      const result = await executeRecaptcha();
      return result.success && result.token ? result.token : null;
    };
    
    setGlobalTokenRefreshFn(tokenRefreshFn);
    registerRecaptchaSiteKey(recaptchaConfig.siteKey);
    
    return () => {
      // Cleanup: unregister when component unmounts
      setGlobalTokenRefreshFn(null);
    };
  }, [executeRecaptcha]);

  const resetRecaptcha = useCallback(() => {
    if (isExecutingRef.current) {
      console.warn('[RecaptchaProvider] Reset skipped - execution in progress');
      return;
    }
    
    resetRecaptchaBase();
    setCurrentTokenState(null);
    currentTokenRef.current = null;
    setError(null);
  }, [resetRecaptchaBase]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const notifyWidgetLoaded = useCallback(() => {
    console.log('[RecaptchaProvider] Widget loaded notification received');
    setIsWidgetLoaded(true);
  }, []);

  console.log('[RecaptchaProvider] Hook initialized:', { 
    refExists: !!recaptchaRef, 
    refCurrent: !!recaptchaRef?.current,
    isReady: isReady(),
    hasCurrentToken: !!currentToken,
    isWidgetLoaded
  });

  const contextValue: RecaptchaContextType = useMemo(() => ({
    executeRecaptcha,
    resetRecaptcha,
    isReady,
    recaptchaRef,
    isEnabled: recaptchaConfig.enabled,
    siteKey: recaptchaConfig.siteKey,
    currentToken,
    setCurrentToken,
    error,
    clearError,
    notifyWidgetLoaded
  }), [executeRecaptcha, resetRecaptcha, isReady, recaptchaRef, recaptchaConfig.enabled, recaptchaConfig.siteKey, currentToken, setCurrentToken, error, clearError, notifyWidgetLoaded]);

  return (
    <RecaptchaContext.Provider value={contextValue}>
      {children}
      <RecaptchaRenderer />
    </RecaptchaContext.Provider>
  );
};

export const useRecaptchaContext = (): RecaptchaContextType => {
  const context = useContext(RecaptchaContext);
  if (!context) {
    throw new Error('useRecaptchaContext must be used within a RecaptchaProvider');
  }
  return context;
};

export default RecaptchaContext;
